/**
 * Área DEPOIMENTOS: a curadoria dos depoimentos que as clientes autorizaram no Questionário
 * Avaliativo.
 *
 * Cobre as duas chamadas do legado (`admin.html:3666-3712`): a lista e a marca "Aprovado pra uso".
 * O consentimento (`autoriza_depoimento`) é da cliente e não se toca aqui; a curadoria
 * (`aprovado_para_uso`) é decisão da consultora, como explica o comentário de `admin.html:3666`.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  clienteDe,
  executar,
  type FonteDeDados,
  type LinhaLeadsFunil,
  type LinhaQuestionarioAvaliativo,
  type Resultado,
} from '../nucleo.js';

/** O depoimento como a lista o lê: as colunas pedidas e o lead embutido pela chave estrangeira. */
export type Depoimento = Pick<
  LinhaQuestionarioAvaliativo,
  'id' | 'depoimento_texto' | 'depoimento_nota' | 'naturalidade' | 'aprovado_para_uso' | 'criado_em'
> & {
  // O PostgREST devolve o lead embutido como objeto (muitos-para-um), ou `null` sem lead.
  readonly leads_funil: Pick<LinhaLeadsFunil, 'nome' | 'email'> | null;
};

/**
 * Lê os depoimentos autorizados e com texto, os mais recentes primeiro.
 * Legado: `admin.html:3673-3678`, `.eq('autoriza_depoimento', true).not('depoimento_texto', 'is',
 * null).order('criado_em', { ascending: false })`.
 *
 * O RLS decide quem lê: a policy de `questionario_avaliativo` dá SELECT a administrador, consulta
 * e à própria cliente, e por isso o legado esconde a área de marketing (`admin.html:3433-3446`).
 */
export function listarDepoimentos(fonte: FonteDeDados): Promise<Resultado<Depoimento[]>> {
  return executar<Depoimento[]>(
    clienteDe(fonte)
      .from('questionario_avaliativo')
      // As mesmas colunas, com nome e e-mail do lead embutidos.
      .select('id, depoimento_texto, depoimento_nota, naturalidade, aprovado_para_uso, criado_em, leads_funil(nome, email)')
      // Só quem autorizou o uso do depoimento.
      .eq('autoriza_depoimento', true)
      // Autorização sem texto não vira cartão vazio.
      .not('depoimento_texto', 'is', null)
      // O mais recente no topo.
      .order('criado_em', { ascending: false }),
  );
}

/**
 * Grava a marca "Aprovado pra uso (funil/marketing)" de um depoimento.
 * Legado: `admin.html:3706`, `sb.from('questionario_avaliativo').update({ aprovado_para_uso })
 * .eq('id', id)`.
 */
export function marcarDepoimentoAprovado(fonte: FonteDeDados, id: number, aprovado: boolean): Promise<Resultado<null>> {
  // Só a coluna da curadoria, e nada mais do questionário.
  return executar<null>(clienteDe(fonte).from('questionario_avaliativo').update({ aprovado_para_uso: aprovado }).eq('id', id));
}
